import React, { useEffect, useState, useRef, useCallback } from "react"
import clsx from "clsx"
import _ from "underscore"
import { useSearchParams, useNavigate } from "react-router-dom"
import "react-day-picker/style.css"
import { DayPicker } from "react-day-picker"
import { ptBR } from "react-day-picker/locale"
import { toast } from "react-toastify"

import styles from "./finance.module.css"
import { financeInstitution, getSavedFilterById } from "../../../services/finance"
import { insightsFilters } from "../../../services/insights";
import { usePdfExport } from "../../../hooks/usePdfExport";
import logoLoginImg from "../../../assets/logo-login.svg";
import { useInstitution } from "../../../context/InstitutionContext"
import { useMantenedora, VIEW_MODES } from "../../../context/MantenedoraContext"

import {
  formatDate,
  addPorcentage,
  formatNumberToCurrency
} from "../../../utils/formatter"
import useOutsideAlerter from "../../../utils/clickOutside"

import Button from "../../../components/button/button"
import ZeroData from "../../../components/zeroData/zeroData"
import HeaderTitle from "../../../components/header/headerTitle/headerTitle"
import FinanceIcon from "../../../assets/icons/finance-icon.svg"

import FinanceCard from "../../../components/finance/financeCard/financeCard"
import FinanceDebts from "../../../components/finance/financeDebts/financeDebts"
import FinanceInvoicing from "../../../components/finance/financeInvoicing/financeInvoicing"
import FinanceAgreements from "../../../components/finance/financeAgreements/financeAgreements"
import FinanceDeliquency from "../../../components/finance/financeDeliquency/financeDeliquency"
import FinanceDebtsPeriod from "../../../components/finance/financeDebtsPeriod/financeDebtsPeriod"
import FinanceActiveStudentsEvolution from "../../../components/finance/financeActiveStudentsEvolution/financeActiveStudentsEvolution"
import FinanceFutureEntries from "../../../components/finance/financeFutureEntries/financeFutureEntries"
import FinanceRevenueComparison from "../../../components/finance/financeRevenueComparison/financeRevenueComparison"
import CourseRiskHeatmap from "../../../components/finance/courseRiskHeatmap/courseRiskHeatmap"
import StudentsTreemap from "../../../components/education/studentsTreemap/studentsTreemap"

import TicketMedioCursoTable from "../../../components/insights/ticketMedioCursoTable/ticketMedioCursoTable"
import TopStudentsRatesTable from "../../../components/insights/topStudentsRatesTable/topStudentsRatesTable"
import OverdueIssuedByCourseTable from "../../../components/insights/overdueIssuedByCourseTable/overdueIssuedByCourseTable"
import OverdueValueByCourseTable from "../../../components/insights/overdueValueByCourseTable/overdueValueByCourseTable"
import MonthsHighestDelinquencyTable from "../../../components/insights/monthsHighestDelinquencyTable/monthsHighestDelinquencyTable"
import MonthsHighestRevenueTable from "../../../components/insights/monthsHighestRevenueTable/monthsHighestRevenueTable"
import CoursesHighestRevenueTable from "../../../components/insights/coursesHighestRevenueTable/coursesHighestRevenueTable"

import CalendarIcon from "../../../assets/icons/calendar.svg"
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg"
import CalendarDayIcon from "../../../assets/icons/calendar-day.svg"
import FileExportIcon from "../../../assets/icons/file-export.svg"

import ChartMixedPorcentageIcon from "../../../assets/icons/chart-mixed-porcentage.svg"
import ChartMixedUpCircleDollarIcon from "../../../assets/icons/chart-mixed-up-circle-dollar.svg"
import ChartMixedDownCircleDollarIcon from "../../../assets/icons/chart-mixed-down-circle-dollar.svg"

import ChartUserSearchIcon from "../../../assets/icons/chart-user-search.svg"
import EnvelopeOpenDollarIcon from "../../../assets/icons/envelope-open-dollar.svg"
import MagnifyingGlassIcon from "../../../assets/icons/magnifying-glass-blue.svg"

import BookIcon from '../../../assets/icons/book.svg';
import DiscIcon from '../../../assets/icons/discipline.svg';
import CourseIcon from '../../../assets/icons/course.svg';
import LocaleIcon from '../../../assets/icons/locale.svg';
import ClassesIcon from '../../../assets/icons/classes.svg';
import FilterSelect from "../../../components/predictive/filterSelect/filterSelect";
import SavedFiltersModal from "../../../components/filters/savedFiltersModal/savedFiltersModal";
import SaveFilterModal from "../../../components/filters/saveFilterModal/saveFilterModal";
import loadingGif from "../../../assets/loading.gif";
import { saveSharedFilters, getSharedFilters, clearSharedFilters, getDateRange, saveDateRange } from "../../../utils/localStorage";

const Finance = () => {
  const { institution } = useInstitution()
  const { 
    viewMode, 
    selectedInstitutionId, 
    institutions, 
    isConsolidatedView
  } = useMantenedora()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Determinar qual institution ID usar para as queries
  const getEffectiveInstitutionId = useCallback(() => {
    // Se está no modo consolidado, retorna null (será tratado diferentemente)
    if (isConsolidatedView) {
      return null;
    }
    // Se há um ID selecionado no contexto da mantenedora, usa ele
    if (selectedInstitutionId) {
      return selectedInstitutionId;
    }
    // Fallback para o contexto de institution tradicional
    return institution?.id;
  }, [isConsolidatedView, selectedInstitutionId, institution?.id]);

  const calendarRef = useRef(null)
  const financeContentRef = useRef(null)
  const { exportPdf, exportingPdf } = usePdfExport()

  const [pdfExpandTables, setPdfExpandTables] = useState(false)

  const [finance, setFinance] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [ticketDrill, setTicketDrill] = useState(null)

  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState("");
  const [knowledgeAreasLoading, setKnowledgeAreasLoading] = useState(false);
  const [knowledgeAreasFetched, setKnowledgeAreasFetched] = useState(false);

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesFetched, setdisciplinesFetched] = useState(false);

  const [courseTypes, setCourseTypes] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [courseTypesLoading, setCourseTypesLoading] = useState(false);
  const [courseTypesFetched, setcourseTypesFetched] = useState(false);

  const [courseGrades, setCourseGrades] = useState([]);
  const [selectedCourseGrade, setSelectedCourseGrade] = useState("");
  const [courseGradesLoading, setCourseGradesLoading] = useState(false);
  const [courseGradesFetched, setcourseGradesFetched] = useState(false);

  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setlocalitiesFetched] = useState(false);

  const [modalities, setModalities] = useState([]);
  const [selectedModality, setSelectedModality] = useState("");
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalitiesFetched, setModalitiesFetched] = useState(false);

  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState("");
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsFetched, setShiftsFetched] = useState(false);

  const [showSavedFiltersModal, setShowSavedFiltersModal] = useState(false);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);

  const [suggestionRange, setSuggestionRange] = useState("")
  const [customMonths, setCustomMonths] = useState("24")
  const [customYears, setCustomYears] = useState("7")
  const [dateRangeString, setDateRangeString] = useState("")
  const [dateRange, setDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false))

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange])

  useEffect(() => {
    // Só buscar dados iniciais se não houver filterId na URL
    const urlFilterId = searchParams.get('filterId');
    
    if (!urlFilterId && initialLoadComplete) {
      fetchInstitutionFinance({
        startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
        endDate: formatDate(dateRange?.to).replaceAll("/", "-"),
        filtermodality: selectedModality,
        filtershift: selectedShift
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete])

  // Refazer fetch quando a instituição selecionada ou modo de visualização muda
  useEffect(() => {
    if (initialLoadComplete) {
      fetchInstitutionFinance({
        startDate: formatDate(dateRange?.from).replaceAll("/", "-"),
        endDate: formatDate(dateRange?.to).replaceAll("/", "-"),
        knowledgeArea: selectedKnowledgeArea,
        discipline: selectedDiscipline,
        courseTypeId: selectedCourseType,
        courseGradeId: selectedCourseGrade,
        localityId: selectedLocality,
        filtermodality: selectedModality,
        filtershift: selectedShift
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedInstitutionId])

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange?.from)} - ${formatDate(dateRange?.to)}`
    )
  }, [dateRange])

  // Salvar filtros no localStorage sempre que houver mudanças (incluindo data sempre)
  useEffect(() => {
    // Só salvar se já tiver feito o carregamento inicial
    if (!initialLoadComplete) return;
    
    const filtersToSave = {
      source: "Financeiro",
      dateRange: {
        from: dateRange?.from ? dateRange.from.toISOString() : null,
        to: dateRange?.to ? dateRange.to.toISOString() : null
      },
      filters: {
        courseType: selectedCourseType || "",
        knowledgeArea: selectedKnowledgeArea || "",
        discipline: selectedDiscipline || "",
        locality: selectedLocality || "",
        modality: selectedModality || "",
        shift: selectedShift || ""
      }
    };
    saveSharedFilters(filtersToSave);
  }, [
    initialLoadComplete,
    selectedCourseType, 
    selectedKnowledgeArea, 
    selectedDiscipline, 
    selectedLocality, 
    selectedModality, 
    selectedShift,
    dateRange
  ])

  // Inicializar filtro da URL se houver filterId
  useEffect(() => {
    const urlFilterId = searchParams.get('filterId');
    
    if (urlFilterId && institution?.id && !initialLoadComplete) {
      const fetchFilterById = async () => {
        try {
          const savedFilter = await getSavedFilterById({ 
            institutionId: institution.id, 
            filterId: urlFilterId 
          });
          
          console.log("Filtro carregado da URL:", savedFilter);
          
          // Parse do filterJson
          const filterData = typeof savedFilter.filterJson === 'string' 
            ? JSON.parse(savedFilter.filterJson) 
            : savedFilter.filterJson;
          
          // Preparar datas para busca
          let fetchStartDate = formatDate(dateRange.from).replaceAll("/", "-");
          let fetchEndDate = formatDate(dateRange.to).replaceAll("/", "-");
          
          // Aplicar filtros de data
          if (filterData.dateRange) {
            const newDateRange = {};
            
            if (filterData.dateRange.startDate) {
              const [day, month, year] = filterData.dateRange.startDate.split('-');
              newDateRange.from = new Date(year, month - 1, day);
              fetchStartDate = filterData.dateRange.startDate;
            }
            
            if (filterData.dateRange.endDate) {
              const [day, month, year] = filterData.dateRange.endDate.split('-');
              newDateRange.to = new Date(year, month - 1, day);
              fetchEndDate = filterData.dateRange.endDate;
            }
            
            if (newDateRange.from || newDateRange.to) {
              setDateRange(prev => ({
                from: newDateRange.from || prev.from,
                to: newDateRange.to || prev.to
              }));
            }
          }

          // Carregar opções das listas antes de aplicar os filtros
          console.log("Carregando opções das listas...");
          
          // Carregar sempre as listas básicas
          if (knowledgeAreas.length === 0) {
            await fetchKnowledgeAreas();
          }
          if (courseTypes.length === 0) {
            await fetchCourseTypes();
          }
          if (modalities.length === 0) {
            await fetchModalities();
          }
          if (shifts.length === 0) {
            await fetchShifts();
          }
          
          // Carregar listas dependentes se houver valores nos filtros
          if (filterData.filters?.knowledgeArea && disciplines.length === 0) {
            await fetchDisciplines();
          }
          if (filterData.filters?.discipline && localities.length === 0) {
            await fetchLocalities();
          }
          
          console.log("Listas carregadas!");

          // Aplicar filtros - SEMPRE atualizar, mesmo se vazio
          if (filterData.filters) {
            console.log("Atualizando campos de filtro da URL:", filterData.filters);
            
            // Atualizar todos os campos, mesmo que vazios
            setSelectedCourseType(filterData.filters.courseType || "");
            setSelectedKnowledgeArea(filterData.filters.knowledgeArea || "");
            setSelectedDiscipline(filterData.filters.discipline || "");
            setSelectedLocality(filterData.filters.locality || "");
            setSelectedModality(filterData.filters.modality || "");
            setSelectedShift(filterData.filters.shift || "");
          }
          
          // Buscar dados com os filtros aplicados
          // O fetchInstitutionFinance já gerencia o loading
          await fetchInstitutionFinance({
            startDate: fetchStartDate,
            endDate: fetchEndDate,
            knowledgeArea: filterData.filters?.knowledgeArea || "",
            discipline: filterData.filters?.discipline || "",
            courseTypeId: filterData.filters?.courseType || "",
            localityId: filterData.filters?.locality || "",
            filtermodality: filterData.filters?.modality || "",
            filtershift: filterData.filters?.shift || ""
          });
          
          toast.success(`Filtro "${savedFilter.name}" aplicado automaticamente!`);
          setInitialLoadComplete(true);
        } catch (error) {
          console.error("Erro ao buscar filtro por ID:", error);
          toast.error("Erro ao carregar filtro da URL");
          setInitialLoadComplete(true);
          setLoading(false);
        }
      };
      
      fetchFilterById();
    } else if (!urlFilterId) {
      // Restaurar filtros do localStorage se existirem
      const sharedFilters = getSharedFilters();
      
      if (sharedFilters && institution?.id) {
        const restoreSharedFilters = async () => {
          try {
            // Aplicar filtros de data
            if (sharedFilters.dateRange) {
              const newDateRange = {};
              
              if (sharedFilters.dateRange.from) {
                newDateRange.from = new Date(sharedFilters.dateRange.from);
              }
              
              if (sharedFilters.dateRange.to) {
                newDateRange.to = new Date(sharedFilters.dateRange.to);
              }
              
              if (newDateRange.from || newDateRange.to) {
                setDateRange(prev => ({
                  from: newDateRange.from || prev.from,
                  to: newDateRange.to || prev.to
                }));
              }
            }

            // Carregar opções das listas antes de aplicar os filtros
            if (sharedFilters.filters?.knowledgeArea && knowledgeAreas.length === 0) {
              await fetchKnowledgeAreas();
            }
            if (sharedFilters.filters?.courseType && courseTypes.length === 0) {
              await fetchCourseTypes();
            }
            if (sharedFilters.filters?.modality && modalities.length === 0) {
              await fetchModalities();
            }
            if (sharedFilters.filters?.shift && shifts.length === 0) {
              await fetchShifts();
            }
            if (sharedFilters.filters?.discipline && disciplines.length === 0) {
              await fetchDisciplines();
            }
            if (sharedFilters.filters?.locality && localities.length === 0) {
              await fetchLocalities();
            }

            // Aplicar filtros
            if (sharedFilters.filters) {
              setSelectedCourseType(sharedFilters.filters.courseType || "");
              setSelectedKnowledgeArea(sharedFilters.filters.knowledgeArea || "");
              setSelectedDiscipline(sharedFilters.filters.discipline || "");
              setSelectedLocality(sharedFilters.filters.locality || "");
              setSelectedModality(sharedFilters.filters.modality || "");
              setSelectedShift(sharedFilters.filters.shift || "");
            }

            // Buscar dados com os filtros restaurados
            const fetchStartDate = sharedFilters.dateRange?.from 
              ? formatDate(new Date(sharedFilters.dateRange.from)).replaceAll("/", "-")
              : formatDate(dateRange.from).replaceAll("/", "-");
            const fetchEndDate = sharedFilters.dateRange?.to 
              ? formatDate(new Date(sharedFilters.dateRange.to)).replaceAll("/", "-")
              : formatDate(dateRange.to).replaceAll("/", "-");

            await fetchInstitutionFinance({
              startDate: fetchStartDate,
              endDate: fetchEndDate,
              knowledgeArea: sharedFilters.filters?.knowledgeArea || "",
              discipline: sharedFilters.filters?.discipline || "",
              courseTypeId: sharedFilters.filters?.courseType || "",
              localityId: sharedFilters.filters?.locality || "",
              filtermodality: sharedFilters.filters?.modality || "",
              filtershift: sharedFilters.filters?.shift || ""
            });

            setInitialLoadComplete(true);
          } catch (error) {
            console.error("Erro ao restaurar filtros do localStorage:", error);
            setInitialLoadComplete(true);
          }
        };

        restoreSharedFilters();
      } else {
        setInitialLoadComplete(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, institution?.id, initialLoadComplete])

  const mergeActiveStudentsEvolutionSeries = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return [];
    if (dataArray.length === 1) return dataArray[0].activeStudentsEvolution || [];
    const map = new Map();
    dataArray.forEach((data) => {
      (data.activeStudentsEvolution || []).forEach((row) => {
        const key = row.yearMonth;
        map.set(key, (map.get(key) || 0) + (Number(row.activeStudentsCount) || 0));
      });
    });
    return [...map.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([yearMonth, activeStudentsCount]) => {
        const y = Math.floor(yearMonth / 100);
        const m = yearMonth % 100;
        const d = new Date(y, m - 1, 1);
        const month = d
          .toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
          .replace(/\.$/, "");
        return { month, yearMonth, activeStudentsCount };
      });
  };

  // Função para agregar dados financeiros de múltiplas instituições
  const aggregateFinanceData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;
    if (dataArray.length === 1) return dataArray[0];

    // Inicializa com o primeiro item como base
    const aggregated = JSON.parse(JSON.stringify(dataArray[0]));

    // Agrega valores numéricos simples
    const sumFields = ['totalRevenue', 'totalExpenses', 'additions', 'totalBilledInPeriod', 'totalReceivedInPeriod'];
    const financialData = aggregated.financialData || {};
    
    for (let i = 1; i < dataArray.length; i++) {
      const data = dataArray[i];
      if (data?.financialData) {
        sumFields.forEach(field => {
          financialData[field] = (financialData[field] || 0) + (data.financialData[field] || 0);
        });
      }
    }

    // Média para taxas
    if (aggregated.financialData?.delinquencyRate) {
      const rateFields = ['installmentDelinquency', 'delinquencyValue', 'overdueValuePercentage', 
                          'delinquencyD1', 'delinquencyD30', 'delinquencyD90'];
      rateFields.forEach(field => {
        let total = 0;
        let count = 0;
        dataArray.forEach(data => {
          if (data?.financialData?.delinquencyRate?.[field] !== undefined) {
            total += data.financialData.delinquencyRate[field];
            count++;
          }
        });
        if (count > 0) {
          aggregated.financialData.delinquencyRate[field] = total / count;
        }
      });
    }

    // Concatena arrays (gráficos, tabelas)
    const arrayFields = ['billed', 'received', 'proportionReceivedIssuedPct', 'agreements',
                         'monthlyDelinquencyRates', 'disciplineIndex', 'topStudentsRates',
                         'overdueIssuedByCourse', 'top10CoursesHighestDelinquency',
                         'top10CoursesLowestDelinquency', 'top10MonthsHighestDelinquency',
                         'top10MonthsLowestDelinquency', 'inadimplenciaAnalysis',
                         'monthsHighestDelinquency', 'monthsHighestRevenue', 'coursesHighestRevenue',
                         'delinquencyBreakdowns', 'futureEntries', 'disciplineIndexMonthly'];

    arrayFields.forEach(field => {
      if (Array.isArray(aggregated[field])) {
        dataArray.slice(1).forEach(data => {
          if (Array.isArray(data?.[field])) {
            aggregated[field] = [...aggregated[field], ...data[field]];
          }
        });
      }
    });

    // Média do ticket médio
    let ticketTotal = 0;
    let ticketCount = 0;
    dataArray.forEach(data => {
      if (data?.averageTicket) {
        ticketTotal += data.averageTicket;
        ticketCount++;
      }
    });
    if (ticketCount > 0) {
      aggregated.averageTicket = ticketTotal / ticketCount;
    }

    aggregated.activeStudentsEvolution = mergeActiveStudentsEvolutionSeries(dataArray);

    aggregated.cancelledStudentsInPeriod = dataArray.reduce(
      (sum, d) => sum + (d.cancelledStudentsInPeriod || 0),
      0
    );

    return aggregated;
  };

  const fetchInstitutionFinance = useCallback(
    _.debounce(async ({
      startDate,
      endDate,
      knowledgeArea,
      discipline,
      courseTypeId,
      courseGradeId,
      localityId,
      filtermodality,
      filtershift,
    }) => {
      try {
        setLoading(true);
        setTicketDrill(null);

        // Determinar quais IDs usar
        const effectiveId = getEffectiveInstitutionId();
        
        if (isConsolidatedView && institutions.length > 0) {
          // Modo consolidado: buscar dados de todas as instituições e agregar
          const promises = institutions.map(inst => 
            financeInstitution({
              institutionId: inst.id,
              startDate,
              endDate,
              knowledgeArea,
              discipline,
              courseTypeId,
              courseGradeId,
              localityId,
              filtermodality,
              filtershift
            }).catch(err => {
              console.warn(`Erro ao buscar dados da instituição ${inst.id}:`, err);
              return null;
            })
          );
          
          const results = await Promise.all(promises);
          const validResults = results
            .filter(r => r !== null && r.financeData)
            .map(r => r.financeData);
          
          const aggregatedData = aggregateFinanceData(validResults);
          setFinance(aggregatedData);
        } else {
          // Modo individual
          const institutionId = effectiveId || institution?.id;
          if (!institutionId) {
            console.warn("Nenhuma instituição selecionada");
            setLoading(false);
            return;
          }
          
          const response = await financeInstitution({
            institutionId,
            startDate,
            endDate,
            knowledgeArea,
            discipline,
            courseTypeId,
            courseGradeId,
            localityId,
            filtermodality,
            filtershift
          });
          setFinance(response.financeData);
        }
        
        setIsCalendarOpen(false);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }, 1000),
    [getEffectiveInstitutionId, isConsolidatedView, institutions, institution?.id]
  );

  const handleKnowledgeAreaChange = (e) => {
    const newKnowledgeArea = e.target.value;

    setSelectedKnowledgeArea(newKnowledgeArea);
    setSelectedDiscipline('');
    setSelectedCourseType('');
    setSelectedCourseGrade('');
    setSelectedLocality('');
    setSelectedModality('');
    setSelectedShift('');

    setDisciplines([]);
    setCourseTypes([]);
    setCourseGrades([]);
    setLocalities([]);
    setModalities([]);
    setShifts([]);

    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: newKnowledgeArea,
      courseTypeId: selectedCourseType,
      filtershift: selectedShift
    });
  };

  const handleDisciplineChange = (e) => {
    setSelectedDiscipline(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: e.target.value,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: selectedShift,
    });
  };

  const handleCourseTypeChange = (e) => {
    setSelectedCourseType(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: e.target.value,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: selectedShift,
    });
  };

  const handleCourseGradeChange = (e) => {
    setSelectedCourseGrade(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: e.target.value,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: selectedShift,
    });
  };

  const handleLocalityChange = (e) => {
    setSelectedLocality(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: e.target.value,
      filtermodality: selectedModality,
      filtershift: selectedShift,
    });
  };

  const handleModalityChange = (e) => {
    setSelectedModality(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: e.target.value,
    });
  };

  const handleShiftChange = (e) => {
    setSelectedShift(e.target.value);
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: e.target.value,
    });
  };

  const fetchKnowledgeAreas = async () => {
    if (knowledgeAreas.length > 0) return;
    try {
      setKnowledgeAreasLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "knowledgearea"
      });

      setKnowledgeAreas(response.filterValues || []);
      setKnowledgeAreasFetched(true);
    } catch (error) {
      console.log("Erro ao buscar áreas de conhecimento", error);
    } finally {
      setKnowledgeAreasLoading(false);
    }
  };

  const fetchDisciplines = async () => {
    if (disciplines.length > 0) return;
    try {
      setDisciplinesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        predictiveFilter: "filterclass",
      });

      setDisciplines(response.filterValues || []);
      setdisciplinesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar disciplinas", error);
    } finally {
      setDisciplinesLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    if (courseTypes.length > 0) return;
    try {
      setCourseTypesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        filterClass: selectedDiscipline,
        predictiveFilter: "filterdegree",
      });

      setCourseTypes(response.filterValues || []);
      setcourseTypesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar tipos de curso", error);
    } finally {
      setCourseTypesLoading(false);
    }
  };

  const fetchCourseGrades = async () => {
    if (courseGrades.length > 0) return;
    try {
      setCourseGradesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "coursegrade",
      });

      setCourseGrades(response.filterValues || []);
      setcourseGradesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar graus de curso", error);
    } finally {
      setCourseGradesLoading(false);
    }
  };

  const fetchLocalities = async () => {
    if (localities.length > 0) return;
    try {
      setLocalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        filterClass: selectedDiscipline,
        predictiveFilter: "filterlocation",
      });

      setLocalities(response.filterValues || []);
      setlocalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar localidades", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  const fetchModalities = async () => {
    if (modalities.length > 0) return;
    try {
      setModalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtermodality",
      });

      setModalities(response.filterValues || []);
      setModalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar modalidades", error);
    } finally {
      setModalitiesLoading(false);
    }
  };

  const fetchShifts = async () => {
    if (shifts.length > 0) return;
    try {
      setShiftsLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtershift",
      });

      setShifts(response.filterValues || []);
      setShiftsFetched(true);
    } catch (error) {
      console.log("Erro ao buscar turnos", error);
    } finally {
      setShiftsLoading(false);
    }
  };

  const selectManualRange = () => {
    setSuggestionRange("")
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: selectedShift
    })
  }

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange)
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = {
          from: new Date(),
          to: new Date()
        };
        break;

      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;

      case "last_30":
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;

      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;

      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;

      case "last_semester":
        const today = new Date();
        const isSecondSemester = today.getMonth() >= 6; // Julho = mês 6 (0-indexed)
        
        if (isSecondSemester) {
          // Estamos no segundo semestre, último semestre = primeiro semestre do ano atual
          selectedRange = {
            from: new Date(`${currentYear}-01-01T00:00:00`),
            to: new Date(`${currentYear}-06-30T23:59:59`)
          };
        } else {
          // Estamos no primeiro semestre, último semestre = segundo semestre do ano anterior
          const lastYear = currentYear - 1;
          selectedRange = {
            from: new Date(`${lastYear}-07-01T00:00:00`),
            to: new Date(`${lastYear}-12-31T23:59:59`)
          };
        }
        break;

      case "last_year": {
        const to = new Date();
        const from = new Date(to.getTime());
        from.setFullYear(from.getFullYear() - 1);
        selectedRange = { from, to };
        break;
      }

      case "last_x_months":
        const monthsBack = parseInt(customMonths, 10);
        if (!isNaN(monthsBack) && monthsBack > 0) {
          const now_lxm = new Date();
          selectedRange = {
            from: new Date(now_lxm.getFullYear(), now_lxm.getMonth() - monthsBack, now_lxm.getDate()),
            to: new Date()
          };
        } else {
          return;
        }
        break;

      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;

      case "last_x_years":
        const yearsBack = parseInt(customYears, 10);
        if (!isNaN(yearsBack) && yearsBack > 0) {
          const startYear = currentYear - yearsBack;
          selectedRange = {
            from: new Date(`${startYear}-01-01T00:00:00`),
            to: new Date(`${currentYear - 1}-12-31T23:59:59`)
          };
        } else {
          return;
        }
        break;

      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;

      case "previous_year":
        const previousYear = currentYear - 1;
        selectedRange = {
          from: new Date(`${previousYear}-01-01T00:00:00`),
          to: new Date(`${previousYear}-12-31T23:59:59`)
        };
        break;

      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDateRange(selectedRange);
    setDateRangeString(
      `${formatDate(selectedRange.from)} - ${formatDate(selectedRange.to)}`
    );
    setIsCalendarOpen(false);

    fetchInstitutionFinance({
      startDate: formatDate(selectedRange.from).replaceAll("/", "-"),
      endDate: formatDate(selectedRange.to).replaceAll("/", "-"),
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      courseGradeId: selectedCourseGrade,
      localityId: selectedLocality,
      filtermodality: selectedModality,
      filtershift: selectedShift
    });
  }

  const handleClearSelection = () => {
    setSuggestionRange("")
    setDateRange({
      from: new Date(),
      to: new Date()
    })
  }

  // Converter filtros atuais para o formato da API de filtros salvos
  const convertFiltersToApiFormat = () => {
    const hasActiveFilters = selectedCourseType || selectedKnowledgeArea || 
      selectedDiscipline || selectedLocality || selectedModality || selectedShift;

    if (!hasActiveFilters && !dateRange?.from && !dateRange?.to) {
      return null;
    }

    const filterData = {
      source: "Financeiro",
      dateRange: {
        startDate: dateRange?.from ? formatDate(dateRange.from).replaceAll("/", "-") : null,
        endDate: dateRange?.to ? formatDate(dateRange.to).replaceAll("/", "-") : null
      },
      filters: {
        courseType: selectedCourseType || "",
        knowledgeArea: selectedKnowledgeArea || "",
        discipline: selectedDiscipline || "",
        locality: selectedLocality || "",
        modality: selectedModality || "",
        shift: selectedShift || ""
      }
    };

    return filterData;
  };

  // Converter filtro salvo da API para o formato local
  const convertApiFilterToLocal = useCallback((savedFilter) => {
    try {
      // Parse do filterJson se for string
      let filterData;
      if (typeof savedFilter.filterJson === 'string') {
        filterData = JSON.parse(savedFilter.filterJson);
      } else {
        filterData = savedFilter.filterJson;
      }

      if (!filterData) return;

      console.log("Aplicando filtros do filtro salvo:", filterData);

      // Aplicar filtros de data
      if (filterData.dateRange) {
        const newDateRange = {};
        
        if (filterData.dateRange.startDate) {
          const [day, month, year] = filterData.dateRange.startDate.split('-');
          newDateRange.from = new Date(year, month - 1, day);
        }
        
        if (filterData.dateRange.endDate) {
          const [day, month, year] = filterData.dateRange.endDate.split('-');
          newDateRange.to = new Date(year, month - 1, day);
        }
        
        if (newDateRange.from || newDateRange.to) {
          setDateRange(prev => ({
            from: newDateRange.from || prev.from,
            to: newDateRange.to || prev.to
          }));
        }
      }

      // Aplicar filtros - SEMPRE atualizar, mesmo se vazio
      if (filterData.filters) {
        console.log("Atualizando campos de filtro:", filterData.filters);
        
        // Atualizar todos os campos, mesmo que vazios
        setSelectedCourseType(filterData.filters.courseType || "");
        setSelectedKnowledgeArea(filterData.filters.knowledgeArea || "");
        setSelectedDiscipline(filterData.filters.discipline || "");
        setSelectedLocality(filterData.filters.locality || "");
        setSelectedModality(filterData.filters.modality || "");
        setSelectedShift(filterData.filters.shift || "");
        
        console.log("Campos atualizados:", {
          courseType: filterData.filters.courseType || "",
          knowledgeArea: filterData.filters.knowledgeArea || "",
          discipline: filterData.filters.discipline || "",
          locality: filterData.filters.locality || "",
          modality: filterData.filters.modality || "",
          shift: filterData.filters.shift || ""
        });
      }

    } catch (error) {
      console.error("Erro ao converter filtro salvo:", error);
      toast.error("Erro ao processar filtro salvo");
    }
  }, []);

  // Abrir modal de filtros salvos
  const handleOpenSavedFilters = () => {
    setShowSavedFiltersModal(true);
  };

  // Abrir modal de salvar filtro
  const handleOpenSaveFilter = () => {
    const filterData = convertFiltersToApiFormat();
    
    console.log("Filter data para salvar:", filterData);
    console.log("Estados atuais:", {
      selectedCourseType,
      selectedKnowledgeArea,
      selectedDiscipline,
      selectedLocality,
      selectedModality,
      selectedShift,
      dateRange
    });
    
    if (!filterData) {
      toast.warning("Adicione pelo menos um filtro ou selecione um período antes de salvar");
      return;
    }
    
    setShowSaveFilterModal(true);
  };

  // Atualizar URL com filterId
  const updateURLWithFilterId = (filterId) => {
    const params = new URLSearchParams();
    if (filterId) {
      params.set('filterId', filterId);
    }
    setSearchParams(params);
  };

  // Aplicar filtro salvo
  const handleApplySavedFilter = async (savedFilter) => {
    console.log("Filtro salvo recebido:", savedFilter);
    
    // Atualizar URL com o filterId
    updateURLWithFilterId(savedFilter.id);
    
    // Parse do filterJson
    const filterData = typeof savedFilter.filterJson === 'string' 
      ? JSON.parse(savedFilter.filterJson) 
      : savedFilter.filterJson;
    
    // Aplicar filtros localmente (atualizar os campos visuais)
    convertApiFilterToLocal(savedFilter);
    
    // Carregar opções das listas de acordo com os filtros aplicados
    if (filterData.filters?.knowledgeArea && knowledgeAreas.length === 0) {
      await fetchKnowledgeAreas();
    }
    if (filterData.filters?.discipline && disciplines.length === 0) {
      await fetchDisciplines();
    }
    if (filterData.filters?.courseType && courseTypes.length === 0) {
      await fetchCourseTypes();
    }
    if (filterData.filters?.locality && localities.length === 0) {
      await fetchLocalities();
    }
    if (filterData.filters?.modality && modalities.length === 0) {
      await fetchModalities();
    }
    if (filterData.filters?.shift && shifts.length === 0) {
      await fetchShifts();
    }
    
    // Buscar dados com os novos filtros aplicados
    setTimeout(() => {
      const startDate = filterData.dateRange?.startDate || formatDate(dateRange.from).replaceAll("/", "-");
      const endDate = filterData.dateRange?.endDate || formatDate(dateRange.to).replaceAll("/", "-");
      
      fetchInstitutionFinance({
        startDate,
        endDate,
        knowledgeArea: filterData.filters?.knowledgeArea || "",
        discipline: filterData.filters?.discipline || "",
        courseTypeId: filterData.filters?.courseType || "",
        localityId: filterData.filters?.locality || "",
        filtermodality: filterData.filters?.modality || "",
        filtershift: filterData.filters?.shift || ""
      });
      
      toast.success(`Filtro "${savedFilter.name}" aplicado com sucesso!`);
    }, 100);
  };

  // Limpar todos os filtros
  const handleClearFilters = () => {
    // Limpar todos os estados de filtros
    setSelectedKnowledgeArea("");
    setSelectedDiscipline("");
    setSelectedCourseType("");
    setSelectedCourseGrade("");
    setSelectedLocality("");
    setSelectedModality("");
    setSelectedShift("");
    
    // Limpar as listas de opções dependentes
    setDisciplines([]);
    setCourseGrades([]);
    setLocalities([]);
    
    // Resetar flags de fetch
    setdisciplinesFetched(false);
    setcourseGradesFetched(false);
    setlocalitiesFetched(false);
    
    // Limpar URL
    setSearchParams(new URLSearchParams());
    
    // Limpar filtros do localStorage
    clearSharedFilters();
    
    // Buscar dados sem filtros
    fetchInstitutionFinance({
      startDate: formatDate(dateRange.from).replaceAll("/", "-"),
      endDate: formatDate(dateRange.to).replaceAll("/", "-"),
      knowledgeArea: "",
      discipline: "",
      courseTypeId: "",
      courseGradeId: "",
      localityId: "",
      filtermodality: "",
      filtershift: ""
    });
    
    toast.success("Filtros limpos com sucesso!");
  };

  const handleExportPDF = async () => {
    if (!finance) {
      toast.warning("Sem dados para exportar");
      return;
    }

    const getLabel = (options, value) => {
      if (!value) return null;
      const opt = options.find((o) => String(o.id ?? o) === String(value));
      return opt ? (opt.name ?? String(opt)) : value;
    };

    const activeFilters = [
      { label: "Período", value: dateRangeString },
      { label: "Tipo de Curso", value: getLabel(courseTypes, selectedCourseType) },
      { label: "Área de Conhecimento", value: getLabel(knowledgeAreas, selectedKnowledgeArea) },
      { label: "Curso", value: getLabel(disciplines, selectedDiscipline) },
      { label: "Localidade", value: getLabel(localities, selectedLocality) },
      { label: "Modalidade", value: getLabel(modalities, selectedModality) },
      { label: "Turno", value: getLabel(shifts, selectedShift) },
    ].filter((f) => f.value);

    const from = formatDate(dateRange?.from).replaceAll("/", "-");
    const to = formatDate(dateRange?.to).replaceAll("/", "-");

    setPdfExpandTables(true);
    // Aguarda o React re-renderizar as tabelas expandidas antes de capturar
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    try {
      await exportPdf([financeContentRef], {
        title: "Relatório Financeiro",
        filename: `financeiro-${from}_${to}.pdf`,
        logoUrl: logoLoginImg,
        filters: activeFilters,
      });
      toast.success("PDF exportado com sucesso!");
    } catch {
      toast.error("Erro ao exportar PDF. Tente novamente.");
    } finally {
      setPdfExpandTables(false);
    }
  };

  const buildDataLinkWithDates = (filterId) => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('filterId', filterId);
    
    // Adicionar datas se existirem
    if (dateRange?.from) {
      const startDate = formatDate(dateRange.from).replaceAll("/", "-");
      params.set('startDate', startDate);
    }
    if (dateRange?.to) {
      const endDate = formatDate(dateRange.to).replaceAll("/", "-");
      params.set('endDate', endDate);
    }
    
    // Adicionar filtros adicionais se existirem
    if (selectedKnowledgeArea) {
      params.set('knowledgeArea', selectedKnowledgeArea);
    }
    if (selectedDiscipline) {
      params.set('discipline', selectedDiscipline);
    }
    if (selectedCourseType) {
      params.set('courseType', selectedCourseType);
    }
    if (selectedCourseGrade) {
      params.set('courseGrade', selectedCourseGrade);
    }
    if (selectedLocality) {
      params.set('locality', selectedLocality);
    }
    if (selectedModality) {
      params.set('modality', selectedModality);
    }
    if (selectedShift) {
      params.set('shift', selectedShift);
    }
    
    return `/app/dados-consolidados?${params.toString()}`;
  };

  const buildDataLinkFromFilters = (queryName) => {
    if (!finance?.appliedFilters) return null;
    
    const filterData = finance.appliedFilters.find(f => f.query === queryName);
    if (!filterData || !filterData.predicates) return null;

    const params = new URLSearchParams({ page: '1' });
    let filterIndex = 0;
    const groupId = 0; // Todos os filtros vão para o mesmo grupo (condição AND)

    // Coletar todos os filtros primeiro
    const collectedFilters = [];

    filterData.predicates.forEach((predicate) => {
      // Parse predicados do tipo "field = 'value'" ou "field IN ('value1', 'value2')"
      
      // Caso: field = 'value' (operador eq)
      const singleMatch = predicate.match(/(\w+)\.(\w+)\s*=\s*'([^']+)'/);
      if (singleMatch) {
        const field = singleMatch[2];
        const value = singleMatch[3];
        
        collectedFilters.push({
          field,
          operator: 'eq',
          values: [value]
        });
        return;
      }

      // Caso: field IN ('value1', 'value2') (operador in)
      const inMatch = predicate.match(/(\w+)\.(\w+)\s+IN\s*\(([^)]+)\)/);
      if (inMatch) {
        const field = inMatch[2];
        const valuesStr = inMatch[3];
        // Remove aspas e separa os valores
        const values = valuesStr.split(',').map(v => v.trim().replace(/'/g, ''));
        
        // Para queries de inadimplência, usar status específicos
        let filteredValues = values;
        if (queryName === 'DelinquencyValue' && field === 'AccountReceiveStatus') {
          // Para inadimplência, usar 'EM ABERTO', 'RECEBIDO' e 'VENCIDO'
          filteredValues = ['RECEBIDO', 'VENCIDO', 'EM ABERTO'];
        }
        
        if (filteredValues.length > 0) {
          collectedFilters.push({
            field,
            operator: 'in',
            values: filteredValues
          });
        }
      }
    });

    // Para query de inadimplência, adicionar filtro de DueDate < endDate
    if (queryName === 'DelinquencyValue' && dateRange?.to) {
      // Formatar data para YYYY-MM-DD
      const endDate = dateRange.to;
      const formattedDate = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      collectedFilters.push({
        field: 'DueDate',
        operator: 'lt',
        values: [formattedDate]
      });
    }

    // Ordenar filtros na ordem desejada: AccountReceiveStatus -> DueDate -> BillType -> outros
    const filterOrder = ['AccountReceiveStatus', 'DueDate', 'BillType'];
    collectedFilters.sort((a, b) => {
      const indexA = filterOrder.indexOf(a.field);
      const indexB = filterOrder.indexOf(b.field);
      
      // Se ambos estão na lista de ordem, ordenar por posição
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // Se apenas A está na lista, A vem primeiro
      if (indexA !== -1) return -1;
      // Se apenas B está na lista, B vem primeiro
      if (indexB !== -1) return 1;
      // Se nenhum está na lista, manter ordem original
      return 0;
    });

    // Adicionar filtros ordenados aos parâmetros
    collectedFilters.forEach((filter, index) => {
      params.append(`filter${filterIndex}_field`, filter.field);
      params.append(`filter${filterIndex}_operator`, filter.operator);
      params.append(`filter${filterIndex}_values`, JSON.stringify(filter.values));
      params.append(`filter${filterIndex}_groupId`, groupId.toString());
      
      // Adicionar condição AND para filtros após o primeiro
      if (filterIndex > 0) {
        params.append(`filter${filterIndex}_condition`, 'AND');
      }
      
      filterIndex++;
    });

    return `/app/dados-consolidados?${params.toString()}`;
  };

  // Linhas do drill mensal do treemap: um item por mês do período filtrado.
  // Para "Outros" (vários cursos) o valor é a média dos tickets dos cursos no mês.
  const buildTicketDrillRows = () => {
    if (!ticketDrill || !dateRange?.from || !dateRange?.to) {
      return [];
    }
    const monthly = finance?.disciplineIndexMonthly || [];
    const titles = new Set((ticketDrill.values || []).map((v) => String(v).toLowerCase()));
    const rows = [];
    const cursor = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1);
    const end = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), 1);
    while (cursor <= end) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth() + 1;
      const matches = monthly.filter(
        (r) => r.year === year && r.month === month && titles.has(String(r.courseTitle).toLowerCase())
      );
      const value = matches.length
        ? matches.reduce((s, r) => s + (Number(r.ticketMedio) || 0), 0) / matches.length
        : null;
      rows.push({ year, month, value });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return rows;
  };

  const drillMonthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  return (
    <div>
      <HeaderTitle title="Financeiro" icon={FinanceIcon}>
      </HeaderTitle>
      <div className={styles.filterWrapper}>
        <div className={styles.filterContainer}>
          <FilterSelect
            icon={CourseIcon}
            label="Tipo de Curso"
            value={selectedCourseType}
            loading={courseTypesLoading}
            options={courseTypes}
            onFocus={fetchCourseTypes}
            hasFetched={courseTypesFetched}
            onChange={handleCourseTypeChange}
          />
          <FilterSelect
            icon={BookIcon}
            label="Área de Conhecimento"
            value={selectedKnowledgeArea}
            loading={knowledgeAreasLoading}
            options={knowledgeAreas}
            onFocus={fetchKnowledgeAreas}
            hasFetched={knowledgeAreasFetched}
            onChange={handleKnowledgeAreaChange}
          />
          <FilterSelect
            icon={DiscIcon}
            label="Curso"
            value={selectedDiscipline}
            loading={disciplinesLoading}
            options={disciplines}
            onFocus={fetchDisciplines}
            hasFetched={disciplinesFetched}
            onChange={handleDisciplineChange}
          />
          <FilterSelect
            icon={LocaleIcon}
            label="Localidade"
            value={selectedLocality}
            loading={localitiesLoading}
            options={localities}
            onFocus={fetchLocalities}
            hasFetched={localitiesFetched}
            onChange={handleLocalityChange}
          />
          <FilterSelect
            icon={ClassesIcon}
            label="Modalidade"
            value={selectedModality}
            loading={modalitiesLoading}
            options={modalities}
            onFocus={fetchModalities}
            hasFetched={modalitiesFetched}
            onChange={handleModalityChange}
          />
          <FilterSelect
            icon={CalendarDayIcon}
            label="Turno"
            value={selectedShift}
            loading={shiftsLoading}
            options={shifts}
            onFocus={fetchShifts}
            hasFetched={shiftsFetched}
            onChange={handleShiftChange}
          />
        </div>

        <div className={styles.headerTitleActions}>
          <Button
            icon={BarsFilterIcon}
            text="Filtros Salvos"
            size="normal"
            variation="outline"
            onClick={handleOpenSavedFilters}
          />

          <Button
            icon={FileExportIcon}
            text="Exportar PDF"
            size="normal"
            variation="outline"
            loading={exportingPdf}
            disabled={!finance}
            onClick={handleExportPDF}
          />

          <Button
            icon={BarsFilterIcon}
            text="Salvar Filtro"
            size="normal"
            variation="primary"
            onClick={handleOpenSaveFilter}
          />

          <Button
            icon={BarsFilterIcon}
            text="Limpar Filtros"
            size="normal"
            variation="secondary"
            onClick={handleClearFilters}
          />

          <div className={`${styles.headerTitleActionsCalendar} .finance-calendar`}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div 
                ref={calendarRef} 
                className={styles.calenderContent}
              >
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !loading && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_semester"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Último Semestre
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_months"
                      })}
                      style={{ opacity: loading ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customMonths}
                        placeholder="24"
                        onChange={(e) => setCustomMonths(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loading && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                      <span
                        style={{ cursor: loading || !customMonths ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loading && customMonths) {
                            selectSuggestionRange("last_x_months");
                          }
                        }}
                      >
                        meses
                      </span>
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("previous_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "previous_year"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Anterior
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      className={clsx(styles.suggestionsItem, styles.customMonthsItem, {
                        [styles.active]: suggestionRange === "last_x_years"
                      })}
                      style={{ opacity: loading ? 0.6 : 1 }}
                    >
                      <span>Últimos</span>
                      <input
                        type="number"
                        min="1"
                        className={styles.customMonthsInput}
                        value={customYears}
                        placeholder="7"
                        onChange={(e) => setCustomYears(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loading && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={loading}
                      />
                      <span
                        style={{ cursor: loading || !customYears ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!loading && customYears) {
                            selectSuggestionRange("last_x_years");
                          }
                        }}
                      >
                        anos
                      </span>
                    </li>
                    <li
                      onClick={() => !loading && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !loading && handleClearSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>

                    <div
                      onClick={() => !loading && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{
                        opacity: loading ? 0.8 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      defaultMonth={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>

                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      defaultMonth={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
      <div ref={financeContentRef} className={`${styles.financeContent} .finance-content`}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px"
            }}
          >
            <img
              src={loadingGif}
              alt="Carregando..."
              style={{ width: "60px", height: "60px" }}
            />
          </div>
        ) : finance && finance.financialData?.totalRevenue === 0
            && (finance.financialData?.totalBilledInPeriod ?? 0) === 0
            && (finance.financialData?.totalReceivedInPeriod ?? 0) === 0
            && (finance.financialData?.delinquencyRate?.delinquencyValue ?? 0) === 0
            && (finance.futureEntries?.length ?? 0) === 0
            && (finance.monthlyDelinquencyRates?.length ?? 0) === 0
            && (finance.agreements?.length ?? 0) === 0 ? (
          <ZeroData>
            <div>
              <h3>Sem dados para o período</h3>
              <p>
                Não encontramos dados para o período filtrado. Altere as datas ou importe dados para esse período{" "}
                <span
                  onClick={() => navigate("/app/importar-dados")}
                  style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                >
                  na página de importação de dados
                </span>.
              </p>
            </div>
          </ZeroData>
        ) : finance ? (
          <>
            <div className={styles.grid_5}>
              <FinanceCard
                title="Receitas"
                value={formatNumberToCurrency(
                  finance.financialData.totalRevenue
                )}
                icon={ChartMixedDownCircleDollarIcon}
                explanation={"A receita é calculada somando todos os valores recebidos de todas as fontes."}
                dataLink={buildDataLinkWithDates('51fa9d0f-f41d-428e-8faf-8d79723fad3f')}
                code="F-01"
              />

              <FinanceCard
                title="Descontos"
                value={formatNumberToCurrency(
                  finance.financialData.totalExpenses
                )}
                icon={ChartMixedUpCircleDollarIcon}
                explanation={"Os descontos somam os descontos nas mensalidades, custos extras e descontos em outras receitas."}
                dataLink={buildDataLinkWithDates('d0a7a9c1-312b-46a6-8121-99927e858223')}
                code="F-02"
              />

              <FinanceCard
                title="Acréscimos"
                value={formatNumberToCurrency(finance.financialData.additions)}
                icon={ChartMixedUpCircleDollarIcon}
                explanation={"Acrescimentos representam valores adicionais somados ao total recebido, incluindo descontos concedidos e valores extras cobrados, como taxas ou acréscimos financeiros."}
                dataLink={buildDataLinkWithDates('7a3788c7-24c6-479e-9a57-69696adff579')}
                code="F-03"
              />

              <FinanceCard
                title="Ticket Médio Mensal"
                value={formatNumberToCurrency(finance.averageTicket)}
                icon={EnvelopeOpenDollarIcon}
                explanation={"O Ticket Médio Mensal é calculado dividindo o valor total recebido em mensalidades pelo número de boletos pagos, mostrando quanto, em média, foi pago por boleto."}
                dataLink={buildDataLinkWithDates('db9ca7be-90fe-4c58-8f80-cb935b6eedda')}
                code="F-04"
              />

              <FinanceCard
                title="Valor faturado"
                value={formatNumberToCurrency(
                  finance.financialData.totalBilledInPeriod ?? 0
                )}
                icon={ChartMixedUpCircleDollarIcon}
                explanation={
                  "Soma do valor emitido (IssuedValue) dos boletos com pagamento registrado na janela filtrada, na mesma base da série Faturado do gráfico Desconto médio sobre o Faturado (agrupado por mês de recebimento)."
                }
                code="F-17"
              />

              <FinanceCard
                title="Valor recebido"
                value={formatNumberToCurrency(
                  finance.financialData.totalReceivedInPeriod ?? 0
                )}
                icon={ChartMixedDownCircleDollarIcon}
                explanation={
                  "Soma dos valores recebidos (ReceivedValue) com data de recebimento entre o início e o fim do período filtrado, mesma base da série Recebido do gráfico Desconto médio sobre o Faturado."
                }
                code="F-20"
              />

            </div>

            <div className={styles.grid_1}>
              <FinanceInvoicing
                received={finance.received}
                billed={finance.billed}
                proportionReceivedIssued={finance.proportionReceivedIssuedPct}
                code="F-05"
              />
            </div>

            <div className={styles.grid_1}>
              <FinanceRevenueComparison
                institutionId={getEffectiveInstitutionId() || institution?.id}
                institutions={institutions}
                dateRange={dateRange}
                code="F-22"
              />
            </div>

            {finance.activeStudentsEvolution &&
              finance.activeStudentsEvolution.length > 0 && (
              <div className={styles.grid_1}>
                <FinanceActiveStudentsEvolution
                  series={finance.activeStudentsEvolution}
                  code="F-18"
                />
              </div>
            )}

            <div className={styles.grid_1}>
              <FinanceFutureEntries
                futureEntries={finance.futureEntries || []}
                code="F-21"
              />
            </div>

            <div className={styles.grid_2}>
              <FinanceCard
                title="Mensalidades Inadimplêntes (% - Qtd)"
                value={addPorcentage(finance.financialData.delinquencyRate.installmentDelinquency, "ano", "anos")}
                icon={ChartUserSearchIcon}
                explanation={"A Taxa mensalidades de Inadimplência é calculada dividindo o total de parcelas vencidas e não pagas pelo total de parcelas do período, e multiplicando por 100 para obter o percentual."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-06"
                detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                  year: b.year,
                  semester: b.semester,
                  month: b.month,
                  value: addPorcentage(b.installmentDelinquency)
                }))}
              />
              <FinanceCard
                title="Valor Inadimplente"
                value={formatNumberToCurrency(finance.financialData.delinquencyRate.delinquencyValue)}
                icon={ChartUserSearchIcon}
                explanation={"O valor de inadimplência representa quanto do valor total a receber, no período filtrado, está vinculado a alunos que não efetuaram o pagamento após a data de vencimento."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-07"
                detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                  year: b.year,
                  semester: b.semester,
                  month: b.month,
                  value: formatNumberToCurrency(b.delinquencyValue)
                }))}
              />
              <FinanceCard
                title="Taxa de Inadimplência (R$)"
                value={addPorcentage(finance.financialData.delinquencyRate.overdueValuePercentage, "ano", "anos")}
                icon={ChartUserSearchIcon}
                explanation={"A Taxa de inadimplência representa quantos porcento do valor total a receber, no período filtrado, está vinculado a alunos que ainda não efetuaram o pagamento após a data de vencimento."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-08"
                detailData={(finance.delinquencyBreakdowns || []).map(b => ({
                  year: b.year,
                  semester: b.semester,
                  month: b.month,
                  value: addPorcentage(b.overdueValuePercentage)
                }))}
              />
            </div>

            <div className={styles.grid_3}>
              <FinanceCard
                title="Taxa de Inadimplência D+1"
                value={addPorcentage(finance.financialData.delinquencyRate.delinquencyD1)}
                icon={ChartUserSearchIcon}
                explanation={"Taxa de Inadimplência D+1: Percentual de mensalidades não pagas com atraso de 1 a 29 dias sobre o total de parcelas do período. Cálculo: boletos sem pagamento, vencidos em relação à data de fim do período filtrado, com atraso entre 1 e 29 dias em relação a essa mesma data (não usa a data do dia). O resultado é (quantidade de boletos nessa faixa / total de boletos) × 100."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-13"
              />
              <FinanceCard
                title="Taxa de Inadimplência D+30"
                value={addPorcentage(finance.financialData.delinquencyRate.delinquencyD30)}
                icon={ChartUserSearchIcon}
                explanation={"Taxa de Inadimplência D+30: Percentual de mensalidades não pagas com atraso de 30 a 89 dias sobre o total de parcelas do período. Cálculo: boletos sem pagamento, vencidos em relação à data de fim do período filtrado, com atraso entre 30 e 89 dias em relação a essa mesma data. O resultado é (quantidade de boletos nessa faixa / total de boletos) × 100."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-14"
              />
              <FinanceCard
                title="Taxa de Inadimplência D+90 (MEC)"
                value={addPorcentage(finance.financialData.delinquencyRate.delinquencyD90)}
                icon={ChartUserSearchIcon}
                explanation={"Taxa de Inadimplência D+90 (MEC): Percentual de mensalidades não pagas com atraso de 90 dias ou mais sobre o total de parcelas do período. Cálculo: boletos sem pagamento, com atraso de 90 dias ou mais em relação à data de fim do período filtrado. O resultado é (quantidade de boletos nessa faixa / total de boletos) × 100. Requisito obrigatório do MEC para preenchimento de questionários."}
                dataLink={buildDataLinkFromFilters("DelinquencyValue")}
                code="F-15"
              />
            </div>

            <div className={styles.grid_5}>
              <FinanceCard
                title="Alunos cancelados no período"
                value={finance.cancelledStudentsInPeriod ?? 0}
                icon={ChartMixedDownCircleDollarIcon}
                explanation={
                  "Quantidade de alunos distintos com registro de adesão em situação cancelada (status Inativo), com data de matrícula (EnrollmentDate) entre o início e o fim do período filtrado. Mesma base do indicador de cancelados do painel educacional; com filtros de curso/turma, considera-se apenas adesões vinculadas às turmas selecionadas."
                }
                code="F-19"
              />
              <FinanceCard
                title="Alunos Possível Evasor"
                value={finance.possivelEvasorCount ?? 0}
                icon={ChartUserSearchIcon}
                explanation={"Alunos em risco de evasão: status ativo, frequência inferior a 50%, nota menor que a média ou inexistente, e inadimplente. Reúne indicadores acadêmicos e financeiros para priorizar ações de retenção."}
                code="F-16"
              />
            </div>

            <div className={styles.grid_1}>
              <FinanceDeliquency 
                delinquencies={finance.monthlyDelinquencyRates} 
                dateRange={dateRange}
                code="F-09"
              />
            </div>

            {finance.delinquency && (
              <div className={styles.grid_5}>
                <FinanceDebts delinquency={finance.delinquency} code="F-10" />

                <FinanceDebtsPeriod
                  delinquencyPeriod={finance.delinquency}
                  dateRange={dateRange}
                  code="F-11"
                />
              </div>
            )}

            <div className={styles.grid_1}>
              <FinanceAgreements 
                agreements={finance.agreements} 
                dateRange={dateRange}
                code="F-12"
              />    
            </div>

            {/* Treemap + Tabela de Ticket Médio por Curso */}
            {finance.disciplineIndex && (
              <div className={styles.grid_5}>
                <StudentsTreemap
                  title="Ticket Médio por Curso"
                  code="F-23"
                  info="Treemap do ticket médio por curso no período filtrado. O tamanho de cada bloco é proporcional ao ticket médio do curso. O bloco 'Outros' mostra a média dos cursos fora do Top 15. Clique em um curso para ver o ticket médio mês a mês na tabela ao lado."
                  items={finance.disciplineIndex}
                  labelKey="courseTitle"
                  valueKey="ticketMedio"
                  formatValue={formatNumberToCurrency}
                  restAggregate="avg"
                  filterField="CourseTitle"
                  onSelect={setTicketDrill}
                  height="460px"
                />
                <TicketMedioCursoTable
                  disciplineData={finance.disciplineIndex || []}
                  showAll={pdfExpandTables}
                  drill={ticketDrill ? {
                    display: ticketDrill.display + (ticketDrill.display === "Outros" ? " (média dos demais cursos)" : ""),
                    rows: buildTicketDrillRows().map((r) => ({
                      label: `${drillMonthNames[r.month - 1]}/${r.year}`,
                      value: r.value
                    }))
                  } : null}
                  onClearDrill={() => setTicketDrill(null)}
                />
              </div>
            )}

            {/* Mapa de calor: risco de evasão × inadimplência por curso.
                Busca os próprios dados — não vem do payload de finance nem responde aos filtros. */}
            <div className={styles.grid_1}>
              <CourseRiskHeatmap institutionId={getEffectiveInstitutionId()} code="F-24" />
            </div>

            {/* Tabela de Maior Valor em Atraso por Curso */}
            {finance.overdueIssuedByCourse && (
              <div className={styles.grid_1}>
                <OverdueValueByCourseTable
                  overdueData={finance.overdueIssuedByCourse || []}
                  showAll={pdfExpandTables}
                />
              </div>
            )}

            {/* Tabela de Meses com Maior Inadimplência */}
            {finance.monthsHighestDelinquency && (
              <div className={styles.grid_1}>
                <MonthsHighestDelinquencyTable
                  delinquencyData={finance.monthsHighestDelinquency || []}
                  showAll={pdfExpandTables}
                />
              </div>
            )}

            {/* Tabela de Meses com Maior Faturamento */}
            {finance.monthsHighestRevenue && (
              <div className={styles.grid_1}>
                <MonthsHighestRevenueTable
                  revenueData={finance.monthsHighestRevenue || []}
                  showAll={pdfExpandTables}
                />
              </div>
            )}

            {/* Tabela de Cursos com Maior Faturamento */}
            {finance.coursesHighestRevenue && (
              <div className={styles.grid_1}>
                <CoursesHighestRevenueTable
                  coursesData={finance.coursesHighestRevenue || []}
                  showAll={pdfExpandTables}
                />
              </div>
            )}
          </>
        ) : (
          <ZeroData>
            <h4>Você ainda não enviou nenhum indicador financeiro nesse período.</h4>
          </ZeroData>
        )}
      </div>

      {/* Modais */}
      {showSavedFiltersModal && (
        <SavedFiltersModal
          onClose={() => setShowSavedFiltersModal(false)}
          onApplyFilter={handleApplySavedFilter}
          filterSource="Financeiro"
          onCreateFilter={handleOpenSaveFilter}
        />
      )}
      
      {showSaveFilterModal && (
        <SaveFilterModal
          onClose={() => setShowSaveFilterModal(false)}
          onFilterSaved={() => {
            toast.success("Filtro salvo com sucesso!");
            setShowSaveFilterModal(false);
          }}
          filterData={convertFiltersToApiFormat()}
        />
      )}
    </div>
  )
}

export default Finance
